import React from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  GridItem,
  VStack,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Icon,
  Progress,
  Badge,
  Divider,
  Avatar
} from '@chakra-ui/react';
import { Brain, Lightbulb, MessageSquare, ThumbsUp, Users, TrendingUp, Zap } from 'lucide-react';

const DashboardPanel: React.FC = () => {
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const methodistBlue = '#0066CC';
  
  // Stats data
  const stats = [
    { 
      title: "Ideas in Queue", 
      value: "124", 
      change: 8, 
      icon: Lightbulb, 
      color: methodistBlue 
    },
    { 
      title: "Processing Rate", 
      value: "92%", 
      change: 5, 
      icon: Zap, 
      color: "#38A169" // Green
    },
    { 
      title: "AI Analysis Score", 
      value: "87%", 
      change: 3, 
      icon: Brain, 
      color: "#805AD5" // Purple
    },
    { 
      title: "Avg. Review Time", 
      value: "1.8 days", 
      change: -25, 
      icon: TrendingUp, 
      color: "#38A169" // Green - negative change is good here
    }
  ];
  
  // Recent activity data
  const recentActivity = [
    {
      type: "analysis",
      title: "AI Analysis Complete: Wellness Integration Proposal",
      author: "AI Engine",
      department: "Data Science",
      time: "10 minutes ago",
      impact: "High",
      status: "Pending Review"
    },
    {
      type: "review",
      title: "Cross-Domain Impact Assessment",
      author: "Dr. Chen",
      department: "JIMI Support",
      time: "1 hour ago",
      impact: "Medium",
      status: "In Progress"
    },
    {
      type: "implementation",
      title: "Automated Stress Assessment Integration",
      author: "Implementation Team",
      department: "Tech Ops",
      time: "3 hours ago",
      impact: "High",
      status: "Deploying"
    },
    {
      type: "feedback",
      title: "Governance Review Complete",
      author: "Compliance Team",
      department: "Governance",
      time: "5 hours ago",
      impact: "Medium",
      status: "Approved"
    }
  ];
  
  // Implementation funnel data
  const funnelData = [
    { stage: "AI Analysis", count: 124, percent: 100, color: "purple.400" },
    { stage: "JIMI Review", count: 98, percent: 79, color: "blue.400" },
    { stage: "Governance Check", count: 76, percent: 61, color: "orange.400" },
    { stage: "Implementation", count: 52, percent: 42, color: "green.400" }
  ];
  
  return (
    <Grid templateColumns="1fr 300px" gap={4}>
      {/* Main content */}
      <GridItem>
        <VStack spacing={4} align="stretch">
          {/* Stats */}
          <Box>
            <Heading size="sm" mb={3}>Key Metrics</Heading>
            <SimpleGrid columns={4} spacing={4}>
              {stats.map((stat, index) => (
                <Box 
                  key={index} 
                  bg={bgColor} 
                  p={3} 
                  borderRadius="md" 
                  boxShadow="sm"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Flex align="center" mb={2}>
                    <Icon as={stat.icon} color={stat.color} boxSize={5} mr={2} />
                    <Text fontSize="sm" fontWeight="medium" color={textColor}>{stat.title}</Text>
                  </Flex>
                  <Stat>
                    <StatNumber fontSize="xl" fontWeight="bold" color={stat.color}>{stat.value}</StatNumber>
                    <StatHelpText mb={0}>
                      <StatArrow type={stat.change > 0 ? 'increase' : 'decrease'} />
                      {Math.abs(stat.change)}%
                    </StatHelpText>
                  </Stat>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
          
          {/* Recent Activity */}
          <Box>
            <Heading size="sm" mb={3}>Recent Activity</Heading>
            <VStack 
              spacing={2} 
              align="stretch" 
              bg={bgColor} 
              p={3} 
              borderRadius="md" 
              boxShadow="sm"
              borderWidth="1px"
              borderColor={borderColor}
            >
              {recentActivity.map((activity, index) => (
                <React.Fragment key={index}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Icon 
                        as={activity.type === 'analysis' ? Brain : 
                             activity.type === 'review' ? MessageSquare : 
                             activity.type === 'implementation' ? Zap : Lightbulb} 
                        color={activity.type === 'analysis' ? "purple.500" : 
                               activity.type === 'review' ? "blue.500" : 
                               activity.type === 'implementation' ? "green.500" : "orange.500"} 
                        boxSize={4} 
                      />
                      <VStack spacing={0} align="start">
                        <Text fontWeight="medium" fontSize="sm">{activity.title}</Text>
                        <Text fontSize="xs" color="gray.500">{activity.author} • {activity.department}</Text>
                      </VStack>
                    </HStack>
                    <HStack spacing={3}>
                      <Badge 
                        colorScheme={activity.status === 'Implemented' ? "green" : 
                                    activity.status === 'Approved' ? "orange" : 
                                    activity.status === 'Completed' ? "purple" : "blue"}
                        fontSize="xs"
                      >
                        {activity.status}
                      </Badge>
                      <Text fontSize="xs" color="gray.500">{activity.time}</Text>
                    </HStack>
                  </Flex>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </VStack>
          </Box>
          
          {/* Domain Distribution */}
          <Box>
            <Heading size="sm" mb={3}>Domain Distribution & Impact</Heading>
            <SimpleGrid columns={2} spacing={4}>
              {/* Domain Breakdown */}
              <Box 
                bg={bgColor} 
                p={4} 
                borderRadius="md" 
                boxShadow="sm"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="sm" fontWeight="medium" mb={3}>Ideas by Domain</Text>
                <VStack spacing={3} align="stretch">
                  {[
                    { domain: "Resilience", count: 48, color: "blue.400" },
                    { domain: "Culture of Wellness", count: 42, color: "purple.400" },
                    { domain: "Practice Efficiency", count: 34, color: "green.400" }
                  ].map((item, index) => (
                    <Box key={index}>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">{item.domain}</Text>
                        <Text fontSize="sm" fontWeight="medium">{item.count}</Text>
                      </Flex>
                      <Progress value={item.count} max={50} size="sm" colorScheme={item.color.split('.')[0]} />
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* Impact Assessment */}
              <Box 
                bg={bgColor} 
                p={4} 
                borderRadius="md" 
                boxShadow="sm"
                borderWidth="1px"
                borderColor={borderColor}
              >
                <Text fontSize="sm" fontWeight="medium" mb={3}>Impact Assessment</Text>
                <VStack spacing={3} align="stretch">
                  {[
                    { level: "High Impact", count: 32, color: "green.400" },
                    { level: "Medium Impact", count: 56, color: "orange.400" },
                    { level: "Under Analysis", count: 36, color: "blue.400" }
                  ].map((item, index) => (
                    <Box key={index}>
                      <Flex justify="space-between" mb={1}>
                        <Text fontSize="sm">{item.level}</Text>
                        <Text fontSize="sm" fontWeight="medium">{item.count}</Text>
                      </Flex>
                      <Progress value={item.count} max={100} size="sm" colorScheme={item.color.split('.')[0]} />
                    </Box>
                  ))}
                </VStack>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </GridItem>
      
      {/* Sidebar */}
      <GridItem>
        <VStack spacing={4} align="stretch">
          {/* Implementation Funnel */}
          <Box 
            bg={bgColor} 
            p={3} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>Idea Implementation Funnel</Heading>
            <VStack spacing={3} align="stretch">
              {funnelData.map((stage, index) => (
                <Box key={index}>
                  <Flex justify="space-between" mb={1}>
                    <Text fontSize="xs">{stage.stage}</Text>
                    <Text fontSize="xs" fontWeight="bold">{stage.count}</Text>
                  </Flex>
                  <Progress value={stage.percent} size="sm" colorScheme={stage.color.split('.')[0]} borderRadius="full" />
                </Box>
              ))}
            </VStack>
          </Box>
          
          {/* Top Contributors */}
          <Box 
            bg={bgColor} 
            p={3} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>Top Contributors</Heading>
            <VStack spacing={2} align="stretch">
              {[
                { name: "Dr. Maria Rodriguez", department: "Emergency Medicine", contributions: 42, avatar: "MR" },
                { name: "Dr. James Wilson", department: "Internal Medicine", contributions: 38, avatar: "JW" },
                { name: "Dr. Sarah Chen", department: "Psychiatry", contributions: 35, avatar: "SC" },
                { name: "Dr. Robert Johnson", department: "Administration", contributions: 29, avatar: "RJ" },
              ].map((contributor, index) => (
                <Flex key={index} justify="space-between" align="center">
                  <HStack>
                    <Avatar size="xs" name={contributor.name} bg={`hsl(${index * 60}, 70%, 60%)`}>{contributor.avatar}</Avatar>
                    <VStack spacing={0} align="start">
                      <Text fontSize="sm" fontWeight="medium">{contributor.name}</Text>
                      <Text fontSize="xs" color="gray.500">{contributor.department}</Text>
                    </VStack>
                  </HStack>
                  <Badge colorScheme="blue">{contributor.contributions}</Badge>
                </Flex>
              ))}
            </VStack>
          </Box>
          
          {/* Trending Topics */}
          <Box 
            bg={bgColor} 
            p={3} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <Heading size="xs" mb={3}>Trending Topics</Heading>
            <Flex wrap="wrap" gap={2}>
              {[
                { name: "Wellness Integration", count: 87 },
                { name: "EHR Optimization", count: 64 },
                { name: "Mobile Access", count: 52 },
                { name: "AI Assistance", count: 49 },
                { name: "Work-Life Balance", count: 43 },
                { name: "Stress Management", count: 38 },
              ].map((topic, index) => (
                <Badge key={index} colorScheme="blue" variant="outline" px={2} py={1}>
                  {topic.name} ({topic.count})
                </Badge>
              ))}
            </Flex>
          </Box>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default DashboardPanel;
